import { default as unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';

const allowedTags = [
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
  'span',
  'div',
  // Rich blog content: comparison tables + figures.
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'figure',
  'figcaption',
  'img',
  'hr',
];

// A tightly-scoped set of class names authors/templates may use to trigger
// styled callouts. Anything not in this list is dropped (see sanitizeClassNames
// plugin) so class= can never be used as a styling/injection escape hatch.
const ALLOWED_CLASSNAMES = new Set([
  'callout',
  'callout-tip',
  'callout-important',
  'callout-note',
  'quick-answer',
  'pro-tip',
  'comparison-table',
]);

const sanitizeSchema = {
  tagNames: allowedTags,
  attributes: {
    '*': ['className'],
    a: ['href', 'title', 'target', 'rel'],
    // Heading ids power the auto-generated Table of Contents jump links; they
    // are (re)generated by the addHeadingIds plugin below, so we only need to
    // allow the attribute through.
    h2: ['id'],
    h3: ['id'],
    img: ['src', 'alt', 'title', 'width', 'height', 'loading'],
    td: ['colSpan', 'rowSpan'],
    th: ['colSpan', 'rowSpan', 'scope'],
  },
  protocols: {
    href: ['http', 'https', 'mailto', 'tel'],
    // https-only on the https site to avoid mixed-content warnings/blocking.
    src: ['https'],
  },
};

const isExternalLink = href => {
  if (!href || typeof href !== 'string') {
    return false;
  }
  const normalized = href.trim().toLowerCase();
  return normalized.startsWith('http://') || normalized.startsWith('https://');
};

// Slugify heading text to match the TOC helper (articleEnhancements.slugifyHeading).
const slugifyHeadingText = text => {
  const s = (text == null ? '' : String(text))
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || null;
};

// Extract concatenated text content from a rehype node subtree.
const nodeText = node => {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  if (Array.isArray(node.children)) return node.children.map(nodeText).join('');
  return '';
};

// Assign stable, de-duplicated ids to h2/h3 headings so the on-page Table of
// Contents jump links resolve. De-dupe scheme (-2, -3, ...) MUST match
// articleEnhancements.extractToc so anchors line up. Runs AFTER sanitize so the
// ids we set are trusted (not author-controlled).
const addHeadingIds = () => tree => {
  const seen = Object.create(null);
  const visit = node => {
    if (!node) return;
    if (node.type === 'element' && (node.tagName === 'h2' || node.tagName === 'h3')) {
      let base = slugifyHeadingText(nodeText(node));
      if (base) {
        if (seen[base] == null) {
          seen[base] = 1;
        } else {
          seen[base] += 1;
          base = `${base}-${seen[base]}`;
        }
        node.properties = { ...node.properties, id: base };
      }
    }
    if (Array.isArray(node.children)) node.children.forEach(visit);
  };
  visit(tree);
};

// Drop any className not in the allowlist so class= cannot be abused. rehype
// represents class as `properties.className` (an array).
const sanitizeClassNames = () => tree => {
  const visit = node => {
    if (!node) return;
    if (node.type === 'element' && node.properties && node.properties.className) {
      const list = Array.isArray(node.properties.className)
        ? node.properties.className
        : String(node.properties.className).split(/\s+/);
      const kept = list.filter(c => ALLOWED_CLASSNAMES.has(c));
      if (kept.length) {
        node.properties.className = kept;
      } else {
        delete node.properties.className;
      }
    }
    if (Array.isArray(node.children)) node.children.forEach(visit);
  };
  visit(tree);
};

const addExternalLinkAttrs = () => tree => {
  const visit = node => {
    if (!node) return;

    if (node.type === 'element' && node.tagName === 'a') {
      const href = node.properties?.href;
      if (isExternalLink(href)) {
        node.properties = {
          ...node.properties,
          target: '_blank',
          rel: 'noopener noreferrer',
        };
      }
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(visit);
    }
  };

  visit(tree);
};

export const sanitizeHtml = html => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    const file = unified()
      .use(rehypeParse, { fragment: true })
      .use(rehypeSanitize, sanitizeSchema)
      .use(sanitizeClassNames)
      .use(addHeadingIds)
      .use(addExternalLinkAttrs)
      .use(rehypeStringify)
      .processSync(html);

    return String(file);
  } catch (error) {
    return '';
  }
};
